import { Form, Alert } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/shared/hooks/useLanguage';
import { ControlledFormItem, Button } from '@/shared/components/ui/atoms';
import AuthMessage from '@/shared/components/ui/molecules/AuthMessage';
import type { ForgotPasswordFormData, ForgotPasswordFormProps } from './types';
import './ForgotPasswordForm.css';

/**
 * ForgotPasswordForm Organism
 *
 * A form to request password reset via email.
 * Sends an OTP token to the user's email address.
 *
 * ## Features
 * - **Email Validation**: Validates email format
 * - **OTP Request**: Sends verification code to email
 * - **Loading State**: Disables form during submission
 * - **Success/Error Feedback**: Displays API responses
 *
 * @example
 * ```tsx
 * <ForgotPasswordForm
 *   onSubmit={handleForgotPassword}
 *   loading={isLoading}
 *   error={errorMessage}
 *   success={otpSent}
 * />
 * ```
 *
 * @param props - ForgotPasswordForm component props
 * @returns A complete forgot password form with validation
 */
const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  onSubmit,
  loading = false,
  error = null,
  success = false,
  email: successEmail,
  ssoIdentified = false,
  onAccessSso,
}) => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const forgotPasswordSchema = useMemo(
    () =>
      z.object({
        email: z
          .string()
          .min(3, t('Email must have at least 3 characters'))
          .email(t('Invalid email format')),
      }),
    [t]
  );

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    mode: 'onChange',
    defaultValues: {
      email: '',
    },
  });

  const email = useWatch({ control, name: 'email' });

  const isSubmitDisabled = useMemo(() => {
    return (
      loading ||
      isSubmitting ||
      success ||
      !email ||
      email.length < 3 ||
      !isValid
    );
  }, [loading, isSubmitting, success, email, isValid]);

  const handleFormSubmit = async (data: ForgotPasswordFormData) => {
    await onSubmit(data);
  };

  // Renderizar componente de sucesso
  if (success) {
    return (
      <div className="auth-page-container">
        <div className="auth-container">
          <AuthMessage
            subtitle={t('Identity and Fraud Platform')}
            title={t('Email sent to {{email}}', { email: successEmail })}
            description={t(
              'Open your email to access the link sent to reset your password'
            )}
          >
            <Form layout="vertical" className="forgot-password-form">
              <Form.Item noStyle>
                <Button
                  variant="text"
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate('/login')}
                >
                  {t('Back to Login')}
                </Button>
              </Form.Item>
            </Form>
          </AuthMessage>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page-container">
      <div className="auth-container">
        <div className="forgot-password-form-wrapper">
          <div className="forgot-password-form-header">
            <div className="forgot-password-form-header-content">
              <p className="forgot-password-form-header-small">
                {t('Identity and Fraud Platform')}
              </p>
              <h1 className="forgot-password-form-header-title">
                {t('Forgot your password?')}
              </h1>
            </div>

            {!ssoIdentified && (
              <div className="forgot-password-form-header-description">
                <p>
                  {t(
                    'Enter your email associated with your account and we will send you a link to set a new password'
                  )}
                </p>
              </div>
            )}
          </div>

          {ssoIdentified && (
            <Alert
              message={
                <span role="status" aria-live="polite">
                  <span style={{ color: '#ff4d4f', fontWeight: 500 }}>
                    {t('SSO identified:')}
                  </span>{' '}
                  {t(
                    'Your email is linked to a corporate login. Use SSO access to sign in to the platform.'
                  )}
                </span>
              }
              type="error"
              showIcon
              closable
            />
          )}

          {ssoIdentified && (
            <p className="forgot-password-form-sso-helper">
              {t('To access the account, use the SSO login.')}
            </p>
          )}

          {error && !ssoIdentified && (
            <Alert
              message={
                <span>
                  <span style={{ color: '#ff4d4f', fontWeight: 500 }}>
                    {t('Attention!')}
                  </span>{' '}
                  {error}
                </span>
              }
              type="error"
              showIcon
              closable
            />
          )}

          <Form
            layout="vertical"
            onFinish={handleSubmit(handleFormSubmit)}
            noValidate
            className="forgot-password-form"
          >
            <ControlledFormItem
              name="email"
              control={control as never}
              errors={error ? { email: { message: '' } } : {}}
              label={ssoIdentified ? t('Corporate email') : t('Email')}
              inputType="text"
              inputProps={{
                placeholder: t('Enter your e-mail here'),
                autoComplete: 'email',
              }}
            />

            <Form.Item className="forgot-password-form-submit-item">
              {ssoIdentified ? (
                // CA06 — a recuperação não se conclui: a ação primária conduz
                // ao fluxo corporativo sem redigitar o e-mail.
                <Button
                  htmlType="button"
                  variant="primary"
                  size="lg"
                  disabled={!email || loading}
                  onClick={() => onAccessSso?.(email.trim())}
                >
                  {t('Access via SSO')}
                </Button>
              ) : (
                <Button
                  htmlType="submit"
                  variant="primary"
                  size="lg"
                  disabled={isSubmitDisabled}
                >
                  {t('Send')}
                </Button>
              )}
            </Form.Item>

            <Form.Item noStyle>
              {ssoIdentified ? (
                // Figma "[Erro 02]": ação secundária "Contatar administrador".
                // Destino configurável (VITE_SSO_ADMIN_CONTACT_URL); fallback
                // abre o cliente de e-mail do usuário.
                <a
                  href={
                    import.meta.env?.VITE_SSO_ADMIN_CONTACT_URL || 'mailto:'
                  }
                  className="forgot-password-form-contact-admin"
                >
                  {t('Contact administrator')}
                </a>
              ) : (
                <Button
                  variant="text"
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate('/login')}
                  disabled={loading || isSubmitting}
                >
                  {t('Back to Login')}
                </Button>
              )}
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;
